import { useEffect, useState } from 'react';

export default function SSOCallbackPage() {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    async function handleCallback() {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const errorParam = params.get('error');
      const errorDesc = params.get('error_description');

      if (errorParam) {
        setError(errorDesc ?? errorParam);
        setStatus('error');
        return;
      }

      if (!token) {
        setError('No authentication token received. Please try signing in again.');
        setStatus('error');
        return;
      }

      // Store token for Bearer auth — security service validates via TEST_JWT_SECRET
      localStorage.setItem('security_session_token', token);
      setStatus('success');
      // Small delay so user sees success state before redirect
      setTimeout(() => {
        window.location.href = '/security';
      }, 800);
    }

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4">
      <div className="bg-white shadow rounded-lg p-10 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="flex justify-center mb-6">
              <svg className="animate-spin h-12 w-12 text-indigo-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Completing sign-in</h2>
            <p className="text-sm text-gray-500">Verifying your credentials, please wait...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign-in successful</h2>
            <p className="text-sm text-gray-500">Redirecting you to your dashboard...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication failed</h2>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <a
              href="/login"
              className="inline-block bg-indigo-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
            >
              Back to Login
            </a>
          </>
        )}
      </div>
    </div>
  );
}
